import graphql from "babel-plugin-relay/macro";
import { useAtom, useAtomValue } from "jotai";
import { useCallback, useState } from "react";
import { PreloadedQuery, usePreloadedQuery } from "react-relay";

import { RequestBookingWizard } from "@/features/BookingWizard/RequestBookingWizard";
import { agendaQueryInputs_duration } from "@/features/DurationBasedAgendas/atoms/agendaQueryInputs";
import { Box } from "@whoosh/design-system/src/components/primitive";

import { AuthenticationModal } from "../../../../features/Authentication/components/AuthenticationModal";
import { BookingWizard } from "../../../../features/BookingWizard/BookingWizard";
import { JoinBookingModal } from "../../../../features/JoinBookingModal";
import {
  useLockSlots,
  useUnlockAllSlots,
} from "../../../../features/LockSlots";
import { TeeSheet } from "../../../../features/TeeSheet";
import {
  isBookingMultipleTeeTimesAtom,
  maxSelectedTeeTimesAtom,
  selectedTeeTimesAtom,
  teeSheetGroupSizeFilterAtom,
  teeSheetHoleFilterAtom,
  teeSheetRequestBookingInfoAtom,
} from "../../../../features/TeeSheet/atoms";
import { TeeSheetQueryRendererQuery } from "./__generated__/TeeSheetQueryRendererQuery.graphql";
import { getResumableRequestBooking } from "./utils/getResumableRequestBooking";

type TeeSheetQueryRendererProps = {
  queryRef: PreloadedQuery<TeeSheetQueryRendererQuery>;
  selectedDate: string;
  refetchCallback: () => void;
};

type BookingWizardLockState = {
  slotId: string;
  currentDate: string;
  isLockAcquired: boolean;
};

export const TeeSheetQueryRenderer = ({
  queryRef,
  selectedDate,
  refetchCallback,
}: TeeSheetQueryRendererProps) => {
  const data = usePreloadedQuery(TeeSheetQueryRendererQueryString, queryRef);
  const currentSession = data.session;
  const currentProfile = data.session?.currentClientProfile;
  const isLoggedIn = !!currentSession?.user?.id;
  const hasActiveClubProfile = !!currentProfile?.member?.id;

  const [clickedSlotId, setClickedSlotId] = useState<string | null>(null);
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);

  const [clickedQueueId, setClickedQueueId] = useState<string | null>(null);
  const [clickedTime, setClickedTime] = useState<string | null>(null);

  const [bookingWizardLockState, setBookingWizardLockState] =
    useState<BookingWizardLockState | null>(null);

  const dismissBookingWizard = useCallback(() => {
    setBookingWizardLockState(null);
  }, []);

  const [joinBookingSlotId, setJoinBookingSlotId] = useState<string | null>(
    null
  );

  const [requestBookingInfo, setRequestBookingInfo] = useAtom(
    teeSheetRequestBookingInfoAtom
  );

  const isBookingMultipleTeeTimes = useAtomValue(isBookingMultipleTeeTimesAtom);
  const [selectedTeeTimes, setSelectedTeeTimes] = useAtom(selectedTeeTimesAtom);
  const maxSelectedTeeTimes = useAtomValue(maxSelectedTeeTimesAtom);
  const [handleLockSlot] = useLockSlots();
  const [unlockAllSlots] = useUnlockAllSlots();
  const groupSize = useAtomValue(teeSheetGroupSizeFilterAtom);
  const holeCount = useAtomValue(teeSheetHoleFilterAtom);
  const duration = useAtomValue(agendaQueryInputs_duration);

  const isPrivate = currentProfile?.member?.club?.supportsPrivate ?? false;

  const handleBookingWizardClose = useCallback(() => {
    unlockAllSlots(() => {
      setBookingWizardLockState(null);
    });
  }, [unlockAllSlots]);

  /**
   * Handles the new patron booking flow initialization.
   * 1. If we are on a timeslot (not a request). We lock the slot -- and open the booking wizard.
   */
  const handleNewPatronBookingFlowStart = useCallback(
    (slotId: string) => {
      //TODO: Only lock if it's not a request. We will need to pivot on that.
      setBookingWizardLockState({
        slotId,
        currentDate: selectedDate,
        isLockAcquired: false,
      });
      handleLockSlot(
        { timeSlotIds: [slotId] },
        () => {
          setBookingWizardLockState((currentState) =>
            currentState?.slotId === slotId
              ? { ...currentState, isLockAcquired: true }
              : currentState
          );
        },
        () => {
          setBookingWizardLockState((currentState) =>
            currentState?.slotId === slotId ? null : currentState
          );
        },
        false
      );
    },
    [handleLockSlot, selectedDate]
  );

  const handleNewPatronBookingFlowStartMultiple = useCallback(
    (slotId: string) => {
      const isSelected = selectedTeeTimes.includes(slotId);
      if (selectedTeeTimes.length < maxSelectedTeeTimes || isSelected) {
        const newSelected = isSelected
          ? selectedTeeTimes.filter((teeTime) => teeTime !== slotId)
          : [...selectedTeeTimes, slotId];
        setSelectedTeeTimes(newSelected);
        handleLockSlot(
          { timeSlotIds: newSelected },
          () => {},
          () => {},
          false
        );
      }
    },
    [handleLockSlot, maxSelectedTeeTimes, selectedTeeTimes, setSelectedTeeTimes]
  );

  const handleRequestBookingFlowStart = useCallback(
    (queueSlotId: string, time: string) => {
      setRequestBookingInfo({
        queueSlotId,
        requestedTime: time,
      });
    },
    [setRequestBookingInfo]
  );

  const onRowPress = useCallback(
    (slotId: string) => {
      if (isLoggedIn && hasActiveClubProfile) {
        if (isBookingMultipleTeeTimes) {
          handleNewPatronBookingFlowStartMultiple(slotId);
        } else {
          handleNewPatronBookingFlowStart(slotId);
        }
      } else {
        setClickedSlotId(slotId);
        setIsAuthModalOpen(true);
      }
    },
    [
      handleNewPatronBookingFlowStart,
      handleNewPatronBookingFlowStartMultiple,
      hasActiveClubProfile,
      isLoggedIn,
      isBookingMultipleTeeTimes,
    ]
  );

  const onJoinableRowPress = useCallback(
    (slotId: string) => {
      if (isPrivate) {
        setJoinBookingSlotId(slotId);
      } else {
        onRowPress(slotId);
      }
    },
    [setJoinBookingSlotId, onRowPress, isPrivate]
  );
  const handleRequestedRowPress = useCallback(
    (id: string, time: string) => {
      if (isLoggedIn && hasActiveClubProfile) {
        handleRequestBookingFlowStart(id, time);
      } else {
        setClickedQueueId(id);
        setClickedTime(time);
        setIsAuthModalOpen(true);
      }
    },
    [hasActiveClubProfile, isLoggedIn, handleRequestBookingFlowStart]
  );

  const onAuthModalComplete = useCallback(() => {
    const resumableRequestBooking = getResumableRequestBooking(
      clickedQueueId,
      clickedTime
    );

    if (clickedSlotId) {
      refetchCallback();
      if (isBookingMultipleTeeTimes) {
        handleNewPatronBookingFlowStartMultiple(clickedSlotId);
      } else {
        handleNewPatronBookingFlowStart(clickedSlotId);
      }
      setClickedSlotId(null);
    } else if (resumableRequestBooking) {
      refetchCallback();
      handleRequestBookingFlowStart(
        resumableRequestBooking.queueSlotId,
        resumableRequestBooking.requestedTime
      );
      setClickedQueueId(null);
      setClickedTime(null);
    } else {
      refetchCallback();
    }
    setIsAuthModalOpen(false);
  }, [
    clickedQueueId,
    clickedSlotId,
    clickedTime,
    handleNewPatronBookingFlowStart,
    handleNewPatronBookingFlowStartMultiple,
    handleRequestBookingFlowStart,
    isBookingMultipleTeeTimes,
    refetchCallback,
  ]);

  const onAuthModalClose = () => {
    setClickedSlotId(null);
    setClickedQueueId(null);
    setClickedTime(null);
    setIsAuthModalOpen(false);
  };

  const memberId = data.session?.currentClientProfile?.member?.id;

  return (
    <Box flex={1}>
      {data.session && (
        <Box flex={1}>
          <TeeSheet
            memberRef={data.session}
            handleRowPress={onRowPress}
            handleRequestedRowPress={handleRequestedRowPress}
            onJoinableRowPress={onJoinableRowPress}
            isAuthModalOpen={isAuthModalOpen}
            setIsAuthModalOpen={setIsAuthModalOpen}
          />

          <AuthenticationModal
            isOpen={isAuthModalOpen}
            onClose={onAuthModalClose}
            onComplete={onAuthModalComplete}
            clubSlug={
              data.session.currentClientProfile?.member?.club.slug ?? ""
            }
          />
          {memberId ? (
            <BookingWizard
              isOpen={!!bookingWizardLockState?.slotId}
              onDismiss={dismissBookingWizard}
              onCancel={handleBookingWizardClose}
              slotId={bookingWizardLockState?.slotId ?? null}
              initiallyLockedSlotIds={
                bookingWizardLockState?.isLockAcquired
                  ? [bookingWizardLockState.slotId]
                  : []
              }
              isSlotLockAcquired={
                bookingWizardLockState?.isLockAcquired ?? false
              }
              membershipTypeId={
                data.session.currentClientProfile?.member?.membershipType?.id ??
                null
              }
              currentPatronId={memberId}
              currentDate={bookingWizardLockState?.currentDate ?? selectedDate}
              clubSlug={
                data.session.currentClientProfile?.member?.club?.slug ?? ""
              }
              slotType="SINGLE"
              initializationOverides={{
                groupSize: groupSize ?? undefined,
                holeCount: holeCount ?? undefined,
                durationMins: duration ?? undefined,
              }}
            />
          ) : null}
          <RequestBookingWizard
            isOpen={!!requestBookingInfo}
            onClose={() => {
              setRequestBookingInfo(null);
            }}
            slotId={bookingWizardLockState?.slotId ?? null}
            membershipTypeId={
              data.session.currentClientProfile?.member?.membershipType?.id ??
              null
            }
            currentPatronId={
              data.session.currentClientProfile?.member?.id ?? null
            }
            clubSlug={
              data.session.currentClientProfile?.member?.club?.slug ?? ""
            }
            queueSlotId={requestBookingInfo?.queueSlotId ?? ""}
            requestedTime={requestBookingInfo?.requestedTime ?? ""}
            initializationOverides={{
              groupSize: groupSize ?? undefined,
              holeCount: holeCount ?? undefined,
              durationMins: duration ?? undefined,
            }}
          />
          {isPrivate ? (
            <JoinBookingModal
              isOpen={!!joinBookingSlotId}
              onClose={() => {
                setJoinBookingSlotId(null);
              }}
              slotId={joinBookingSlotId ?? ""}
              onRowPress={onRowPress}
              isWaitlistBooking={false}
            />
          ) : null}
        </Box>
      )}
    </Box>
  );
};

export const TeeSheetQueryRendererQueryString = graphql`
  query TeeSheetQueryRendererQuery($date: Date!) {
    session {
      ...TeeSheet_HeaderData
      user {
        id
        email
      }
      currentClientProfile {
        ... on MemberProfile {
          id
        }
        member {
          ... on Member {
            id
          }
          ... on Customer {
            id
          }
          club {
            id
            slug
            supportsPublic
            supportsPrivate
          }
          membershipType {
            id
          }
        }
      }
    }
  }
`;
